/**
 * 파일명: privacyConsent.en.js
 * 설명: 개인정보처리방침 전문 (영문)
 *
 * 주의: 법적 효력의 기준은 한국어 원문(privacyConsent.ko.js)이며 본 영문본은 이해를 돕기 위한 참고 번역입니다.
 */
const privacyConsentEn = {
  title: "Privacy Policy",
  notice:
    "This English version is provided for reference only. In the event of any discrepancy, the Korean original shall prevail.",
  intro: [
    {
      type: "p",
      text: "RIV Technology Co., Ltd. (the “Company”) establishes and discloses the following privacy policy pursuant to Article 30 of the Personal Information Protection Act, in order to protect the personal information of data subjects and to handle related grievances promptly and smoothly.",
    },
    {
      type: "p",
      text: "○ This privacy policy takes effect from August 4, 2026.",
    },
  ],
  sections: [
    {
      title: "Article 1 (Purposes of Processing Personal Information)",
      blocks: [
        {
          type: "p",
          text: "The Company processes personal information for the following purposes. The personal information being processed will not be used for any purpose other than those set out below, and where the purpose of use changes, necessary measures such as obtaining separate consent will be taken in accordance with Article 18 of the Personal Information Protection Act.",
        },
        {
          type: "ol",
          items: [
            "Membership registration and management: verifying intent to register, identifying members and confirming identity, managing access rights at the level of the member's company, processing administrator approvals, and preventing misuse",
            "Provision and operation of the Services: providing workplace management services such as meeting room reservations, meeting room device management, hot-desking, and video conferencing",
            "Customer support: responding to customer inquiries and delivering service notices and announcements",
            "Service improvement: improving service quality through statistical analysis of meeting room and seat usage data (processed in a form in which specific individuals cannot be identified)",
          ],
        },
      ],
    },
    {
      title: "Article 2 (Categories of Personal Information Processed)",
      blocks: [
        {
          type: "p",
          text: "① The Company processes the following categories of personal information in order to provide the Services.",
        },
        { type: "p", text: "1. Membership registration and management" },
        {
          type: "ul",
          items: [
            "Mandatory items: e-mail address, company name, user name, password",
          ],
        },
        {
          type: "p",
          text: "② The following information may be generated and collected automatically in the course of using the Services.",
        },
        {
          type: "ul",
          items: [
            "Access IP address, access date and time, service usage records, browser and device information, cookies",
          ],
        },
      ],
    },
    {
      title:
        "Article 3 (Processing and Retention Periods of Personal Information)",
      blocks: [
        {
          type: "p",
          text: "① The Company processes and retains personal information within the retention and use period prescribed by law, or within the retention and use period consented to by the data subject at the time the personal information was collected.",
        },
        {
          type: "p",
          text: "② Personal information relating to membership registration and management is retained and used for the purposes set out in Article 1 from the date of consent to its collection and use until the member withdraws their membership. The basis for retention is the consent of the data subject (provision of membership-based services and identification of users).",
        },
        {
          type: "p",
          text: "③ Notwithstanding Paragraph 2, where a retention obligation exists under relevant statutes, the information is kept for the period prescribed by those statutes.",
        },
        {
          type: "ul",
          items: [
            "Records concerning consumer complaints or dispute handling: 3 years (Act on the Consumer Protection in Electronic Commerce)",
            "Records concerning payment and the supply of goods: 5 years (Act on the Consumer Protection in Electronic Commerce)",
            "Records concerning contracts or withdrawal of subscription: 5 years (Act on the Consumer Protection in Electronic Commerce)",
            "Records concerning labeling and advertising: 6 months (Act on the Consumer Protection in Electronic Commerce)",
          ],
        },
      ],
    },
    {
      title: "Article 4 (Provision of Personal Information to Third Parties)",
      blocks: [
        {
          type: "p",
          text: "① The Company processes personal information only within the scope specified in Article 1, and provides personal information to third parties only where Article 17 or Article 18 of the Personal Information Protection Act applies, such as with the consent of the data subject or under special provisions of law.",
        },
        {
          type: "p",
          text: "② The Company does not currently provide data subjects' personal information to third parties. Should provision to a third party become necessary, the Company will inform the data subject of the recipient, the purpose of provision, the categories of personal information provided, and the recipient's retention and use period, and will obtain separate consent.",
        },
        {
          type: "p",
          text: "③ An administrator of the company to which a member belongs may, for the purpose of operating the Services, view the name, e-mail address, meeting room reservation records, and seat usage records of members belonging to that company. This follows from members using the Services on a per-company basis and does not constitute provision to a third party.",
        },
      ],
    },
    {
      title:
        "Article 5 (Rights and Obligations of Data Subjects and Their Legal Representatives, and How to Exercise Them)",
      blocks: [
        {
          type: "p",
          text: "① A data subject may exercise rights against the Company at any time, including the right to request access to, correction of, deletion of, or suspension of processing of their personal information.",
        },
        {
          type: "p",
          text: "② The rights under Paragraph 1 may be exercised in writing, by e-mail, or by similar means in accordance with Article 41, Paragraph 1 of the Enforcement Decree of the Personal Information Protection Act, and the Company will act on such requests without delay.",
        },
        {
          type: "p",
          text: "③ The rights under Paragraph 1 may be exercised through an agent, such as the data subject's legal representative or a duly authorized person. In such case, a power of attorney in the form prescribed by the Public Notice on Methods of Processing Personal Information must be submitted.",
        },
        {
          type: "p",
          text: "④ The data subject's rights to request access to personal information and suspension of processing may be restricted pursuant to Article 35, Paragraph 4 and Article 37, Paragraph 2 of the Personal Information Protection Act.",
        },
        {
          type: "p",
          text: "⑤ Where other statutes expressly designate certain personal information as subject to collection, deletion of that personal information may not be requested.",
        },
        {
          type: "p",
          text: "⑥ Where a request for access, correction or deletion, or suspension of processing is made in exercise of a data subject's rights, the Company verifies whether the person making the request is the data subject or a duly authorized agent.",
        },
      ],
    },
    {
      title: "Article 6 (Destruction of Personal Information)",
      blocks: [
        {
          type: "p",
          text: "① The Company destroys the relevant personal information without delay when it becomes unnecessary, such as upon expiry of the retention period or achievement of the purpose of processing.",
        },
        {
          type: "p",
          text: "② Where personal information must continue to be preserved under other statutes even though the retention period consented to by the data subject has expired or the purpose of processing has been achieved, the Company moves such personal information to a separate database or stores it in a different location.",
        },
        {
          type: "p",
          text: "③ The procedure and method for destroying personal information are as follows.",
        },
        {
          type: "p",
          text: "1. Destruction procedure: the Company selects the personal information for which grounds for destruction have arisen and destroys it upon approval of the Company's personal information protection officer.",
        },
        { type: "p", text: "2. Destruction method" },
        {
          type: "p",
          text: "Information in the form of electronic files is destroyed using technical methods that render the records irreproducible.",
        },
        {
          type: "p",
          text: "Personal information printed on paper is destroyed by shredding or incineration.",
        },
      ],
    },
    {
      title: "Article 7 (Measures to Secure the Safety of Personal Information)",
      blocks: [
        {
          type: "p",
          text: "The Company takes the following measures to secure the safety of personal information.",
        },
        {
          type: "ol",
          items: [
            "Regular internal audits: internal audits are conducted regularly (once per quarter) to secure stability in the handling of personal information.",
            "Minimization and training of staff handling personal information: measures are implemented to manage personal information by designating staff who handle personal information and limiting and minimizing them to the persons in charge.",
            "Establishment and implementation of an internal management plan: an internal management plan is established and implemented for the safe processing of personal information.",
            "Technical measures against hacking and the like: the Company installs security programs and performs periodic updates and inspections to prevent the leakage or damage of personal information by hacking or computer viruses, installs its systems in areas with controlled external access, and monitors and blocks access both technically and physically.",
            "Encryption of personal information: users' passwords are stored and managed in encrypted form so that only the user knows them, and separate security functions are used for important data, such as encrypting files and transmitted data or using file lock functions.",
            "Retention of access records and prevention of forgery or alteration: records of access to the personal information processing system are retained and managed for at least one year; where personal information of 50,000 or more data subjects is processed, or where unique identifying information or sensitive information is processed, such records are retained and managed for at least two years. Security functions are also used to prevent access records from being forged, altered, stolen, or lost.",
            "Restriction of access to personal information: necessary measures are taken to control access to personal information through the granting, modification, and revocation of access rights to the database system that processes personal information, and unauthorized external access is controlled using an intrusion prevention system.",
            "Use of locking devices for document security: documents and auxiliary storage media containing personal information are kept in a safe location fitted with a locking device.",
            "Access control for unauthorized persons: a separate physical storage location for personal information is maintained, and access control procedures are established and operated for it.",
          ],
        },
      ],
    },
    {
      title:
        "Article 8 (Installation, Operation, and Refusal of Devices that Automatically Collect Personal Information)",
      blocks: [
        {
          type: "p",
          text: "① The Company uses ‘cookies’, which store usage information and retrieve it from time to time, in order to provide individually tailored services to users.",
        },
        {
          type: "p",
          text: "② A cookie is a small amount of information that the server (http) used to operate the website sends to the user's computer browser, and it may also be stored on the hard disk of the user's PC.",
        },
        {
          type: "p",
          text: "a. Purpose of using cookies: cookies are used to keep users signed in and to provide users with optimized information by identifying visit and usage patterns for each service and website visited, whether a secure connection was used, and the like.",
        },
        {
          type: "p",
          text: "b. Installation, operation, and refusal of cookies: you may refuse the storage of cookies through your web browser's settings menu (Tools > Internet Options > Privacy, or equivalent).",
        },
        {
          type: "p",
          text: "c. If you refuse the storage of cookies, you may experience difficulty using tailored services such as staying signed in.",
        },
      ],
    },
    {
      title: "Article 9 (Personal Information of Children Under 14)",
      blocks: [
        {
          type: "p",
          text: "The Company does not collect the personal information of children under the age of 14, and children under 14 may not register as members of the Services.",
        },
      ],
    },
    {
      title: "Article 10 (Personal Information Protection Officer)",
      blocks: [
        {
          type: "p",
          text: "① The Company is overall responsible for matters relating to the processing of personal information and has designated a personal information protection officer as set out below to handle data subjects' complaints and provide remedies in relation to the processing of personal information.",
        },
        { type: "p", text: "▶ Personal Information Protection Officer" },
        {
          type: "ul",
          items: [
            "Name: Yongjin Kim",
            "Role: CPO",
            "Rank: Director",
            "Contact: yj.kim@riv-tech.co.kr, 070-7798-1311",
          ],
        },
        {
          type: "p",
          text: "※ You will be connected to the department in charge of personal information protection.",
        },
        {
          type: "p",
          text: "▶ Department in Charge of Personal Information Protection",
        },
        {
          type: "ul",
          items: [
            "Department: Information Security Team",
            "Person in charge: Yongjin Kim",
            "Contact: yj.kim@riv-tech.co.kr, 070-7798-1311",
          ],
        },
        {
          type: "p",
          text: "② Data subjects may direct any inquiries, complaints, or requests for remedy relating to personal information protection arising in the course of using the Company's Services to the personal information protection officer or the department in charge. The Company will respond to and handle such inquiries without delay.",
        },
      ],
    },
    {
      title: "Article 11 (Requests to Access Personal Information)",
      blocks: [
        {
          type: "p",
          text: "Data subjects may submit requests to access personal information under Article 35 of the Personal Information Protection Act to the department below. The Company will endeavor to process such access requests promptly.",
        },
        {
          type: "p",
          text: "▶ Department Receiving and Handling Access Requests",
        },
        {
          type: "ul",
          items: [
            "Department: Information Security Team",
            "Person in charge: Yongjin Kim",
            "Contact: yj.kim@riv-tech.co.kr, 070-7798-1311",
          ],
        },
      ],
    },
    {
      title: "Article 12 (Remedies for Infringement of Rights)",
      blocks: [
        {
          type: "p",
          text: "In order to obtain relief from an infringement of personal information, a data subject may apply for dispute resolution or counseling to the Personal Information Dispute Mediation Committee, the Korea Internet & Security Agency's Personal Information Infringement Report Center, and the like. For other reports of, or counseling on, personal information infringement, please contact the organizations below.",
        },
        {
          type: "ol",
          items: [
            "Personal Information Dispute Mediation Committee: 1833-6972 (no area code) (www.kopico.go.kr)",
            "Personal Information Infringement Report Center: 118 (no area code) (privacy.kisa.or.kr)",
            "Supreme Prosecutors' Office: 1301 (no area code) (www.spo.go.kr)",
            "National Police Agency: 182 (no area code) (ecrm.cyber.go.kr)",
          ],
        },
        {
          type: "p",
          text: "A person whose rights or interests are infringed by a disposition taken, or an omission, by the head of a public institution in response to a request under Article 35 (Access to Personal Information), Article 36 (Correction and Deletion of Personal Information), or Article 37 (Suspension of Processing of Personal Information, etc.) of the Personal Information Protection Act may file an administrative appeal as prescribed by the Administrative Appeals Act.",
        },
        {
          type: "p",
          text: "※ For details on administrative appeals, please refer to the website of the Central Administrative Appeals Commission (www.simpan.go.kr).",
        },
      ],
    },
    {
      title: "Article 13 (Changes to the Privacy Policy)",
      blocks: [
        {
          type: "p",
          text: "① This privacy policy takes effect from August 4, 2026.",
        },
        {
          type: "p",
          text: "② Where the Company changes this privacy policy, it will announce the change on the Company website from seven days before it takes effect. Where the change materially affects the rights of data subjects, the announcement will be made from thirty days before it takes effect.",
        },
        {
          type: "p",
          text: "③ If you need a previous version of this privacy policy, you may obtain it by contacting the department in charge of personal information protection.",
        },
      ],
    },
  ],
};

export default privacyConsentEn;
